export type ConsentType = 'analytics' | 'marketing' | 'functional' | 'necessary';

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  necessary: boolean;
}

const CONSENT_COOKIE_NAME = 'tobg-consent';
const CONSENT_EXPIRY_DAYS = 365;

export function getConsentState(): ConsentState {
  if (typeof window === 'undefined') {
    return {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true,
    };
  }

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

  if (!cookie) {
    return {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true,
    };
  }

  try {
    const value = cookie.split('=')[1];
    return JSON.parse(decodeURIComponent(value));
  } catch {
    return {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true,
    };
  }
}

export function setConsentState(consent: ConsentState): void {
  if (typeof window === 'undefined') return;

  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

  const cookieValue = encodeURIComponent(JSON.stringify(consent));
  document.cookie = `${CONSENT_COOKIE_NAME}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;

  updateGoogleConsent(consent);
  
  window.dispatchEvent(new CustomEvent('consentUpdate', { detail: consent }));
}

export function updateGoogleConsent(consent: ConsentState): void {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('consent', 'update', {
    'analytics_storage': consent.analytics ? 'granted' : 'denied',
    'ad_storage': consent.marketing ? 'granted' : 'denied',
    'ad_user_data': consent.marketing ? 'granted' : 'denied',
    'ad_personalization': consent.marketing ? 'granted' : 'denied',
    'functionality_storage': consent.functional ? 'granted' : 'denied',
    'personalization_storage': consent.functional ? 'granted' : 'denied',
    'security_storage': 'granted'
  });
}

export function hasUserConsented(): boolean {
  if (typeof window === 'undefined') return false;
  
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));
    
  return !!cookie;
}

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}